/**
 * Service de gestion des données pour le module de planification d'investissement.
 * Utilise Supabase avec fallback localStorage si les tables n'existent pas.
 * Migration auto: localStorage → Supabase au premier chargement.
 */

import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';

// ─── Supabase CRUD factory ──────────────────────────────────────────

let _supabaseAvailable = null; // null = not checked, true/false

async function checkSupabase() {
  if (_supabaseAvailable !== null) return _supabaseAvailable;
  try {
    const { error } = await supabase.from('planning_entities').select('id').limit(1);
    _supabaseAvailable = !error || error.code !== '42P01'; // 42P01 = table doesn't exist
    return _supabaseAvailable;
  } catch {
    _supabaseAvailable = false;
    return false;
  }
}

// ─── localStorage helpers (fallback) ────────────────────────────────

const PREFIX = 'immoiq_plan';
const genId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

function lsLoad(userId, type) {
  try {
    const raw = localStorage.getItem(`${PREFIX}_${type}_${userId}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function lsSave(userId, type, data) {
  localStorage.setItem(`${PREFIX}_${type}_${userId}`, JSON.stringify(data));
}

function lsClear(userId, type) {
  localStorage.removeItem(`${PREFIX}_${type}_${userId}`);
}

// localStorage CRUD (synchronous, wrapped as async)
function makeLocalCrud(type) {
  return {
    getAll: async (userId) => lsLoad(userId, type),
    add: async (userId, item) => {
      const items = lsLoad(userId, type);
      const newItem = { ...item, id: genId(), createdAt: new Date().toISOString() };
      items.push(newItem);
      lsSave(userId, type, items);
      return newItem;
    },
    update: async (userId, id, updates) => {
      const items = lsLoad(userId, type);
      const idx = items.findIndex(i => i.id === id);
      if (idx === -1) return null;
      items[idx] = { ...items[idx], ...updates, updatedAt: new Date().toISOString() };
      lsSave(userId, type, items);
      return items[idx];
    },
    remove: async (userId, id) => {
      const items = lsLoad(userId, type).filter(i => i.id !== id);
      lsSave(userId, type, items);
    },
  };
}

// ─── Supabase CRUD factory ──────────────────────────────────────────

function makeSupaCrud(tableName, lsType) {
  const fallback = makeLocalCrud(lsType);

  return {
    getAll: async (userId) => {
      if (!(await checkSupabase())) return fallback.getAll(userId);
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
      if (error) { console.error(`[planning] getAll ${tableName}:`, error.message); return []; }
      return data.map(row => ({ ...row.data, id: row.id, createdAt: row.created_at }));
    },

    add: async (userId, item) => {
      if (!(await checkSupabase())) return fallback.add(userId, item);
      const { id: _omit, createdAt: _omit2, updatedAt: _omit3, ...cleanItem } = item;
      const { data, error } = await supabase
        .from(tableName)
        .insert({ user_id: userId, data: cleanItem })
        .select()
        .single();
      if (error) { console.error(`[planning] add ${tableName}:`, error.message); return null; }
      return { ...data.data, id: data.id, createdAt: data.created_at };
    },

    update: async (userId, id, updates) => {
      if (!(await checkSupabase())) return fallback.update(userId, id, updates);
      const { data: current, error: fetchErr } = await supabase
        .from(tableName)
        .select('data')
        .eq('id', id)
        .eq('user_id', userId)
        .single();
      if (fetchErr) { console.error(`[planning] fetch for update:`, fetchErr.message); return null; }
      const { id: _omit, createdAt: _omit2, updatedAt: _omit3, ...cleanUpdates } = updates;
      const merged = { ...current.data, ...cleanUpdates };
      const { data, error } = await supabase
        .from(tableName)
        .update({ data: merged, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();
      if (error) { console.error(`[planning] update ${tableName}:`, error.message); return null; }
      return { ...data.data, id: data.id, createdAt: data.created_at };
    },

    remove: async (userId, id) => {
      if (!(await checkSupabase())) return fallback.remove(userId, id);
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id)
        .eq('user_id', userId);
      if (error) console.error(`[planning] remove ${tableName}:`, error.message);
    },

    // Bulk insert (for migration)
    bulkInsert: async (userId, items) => {
      if (!(await checkSupabase())) return;
      if (!items || items.length === 0) return;
      const rows = items.map(item => {
        const { id: _omit, createdAt: _omit2, updatedAt: _omit3, ...cleanItem } = item;
        return { user_id: userId, data: cleanItem };
      });
      const { error } = await supabase.from(tableName).insert(rows);
      if (error) console.error(`[planning] bulkInsert ${tableName}:`, error.message);
    },
  };
}

// ─── Service instances ──────────────────────────────────────────────

export const entitiesService = makeSupaCrud('planning_entities', 'entities');
export const assetsService = makeSupaCrud('planning_assets', 'assets');
export const debtsService = makeSupaCrud('planning_debts', 'debts');
export const revenuesService = makeSupaCrud('planning_revenues', 'revenues');
export const planEventsService = makeSupaCrud('planning_events', 'plan_events');
export const scenariosService = makeSupaCrud('planning_scenarios', 'scenarios');

// ─── Auto-migration localStorage → Supabase ────────────────────────

export async function migrateLocalStorageToSupabase(userId) {
  if (!(await checkSupabase())) return false;

  const types = [
    { ls: 'entities', service: entitiesService },
    { ls: 'assets', service: assetsService },
    { ls: 'debts', service: debtsService },
    { ls: 'revenues', service: revenuesService },
    { ls: 'plan_events', service: planEventsService },
  ];

  let migrated = false;
  for (const { ls, service } of types) {
    const localData = lsLoad(userId, ls);
    if (localData.length === 0) continue;
    const remoteData = await service.getAll(userId);
    if (remoteData.length > 0) continue; // already has data in Supabase
    await service.bulkInsert(userId, localData);
    lsClear(userId, ls);
    migrated = true;
  }
  return migrated;
}

// ─── Ensure defaults ────────────────────────────────────────────────

export async function ensureDefaultEntities(userId) {
  const entities = await entitiesService.getAll(userId);
  if (entities.length === 0) {
    await entitiesService.add(userId, {
      name: 'Personnel',
      type: 'personnel',
      description: '',
      marginalTaxRate: DEFAULT_TAX_RATES.personnel,
    });
  }
  return entitiesService.getAll(userId);
}

export async function ensureDefaultAssets(userId) {
  const assets = await assetsService.getAll(userId);
  if (assets.length === 0) {
    const entities = await entitiesService.getAll(userId);
    const personal = entities.find(e => e.type === 'personnel');
    if (personal) {
      await assetsService.add(userId, {
        name: 'Compte bancaire',
        type: 'non_enregistre',
        entityId: personal.id,
        currentBalance: 0,
        monthlyContribution: 0,
        avgAnnualReturn: 0,
        notes: '',
      });
    }
  }
  return assetsService.getAll(userId);
}

// ─── Check if Supabase is available (exposed for UI) ────────────────

export async function isSupabaseReady() {
  return checkSupabase();
}

// ─── Mortgage calculator ────────────────────────────────────────────

export function calcMortgage(balance, annualRate, amortYears, monthlyPayment, solveFor) {
  const r = (parseFloat(annualRate) || 0) / 100;
  const semiRate = r / 2;
  const m = Math.pow(1 + semiRate, 1 / 6) - 1;

  switch (solveFor) {
    case 'payment': {
      const P = parseFloat(balance) || 0;
      const n = (parseFloat(amortYears) || 25) * 12;
      if (P <= 0 || m <= 0 || n <= 0) return 0;
      return P * (m * Math.pow(1 + m, n)) / (Math.pow(1 + m, n) - 1);
    }
    case 'balance': {
      const PMT = parseFloat(monthlyPayment) || 0;
      const n = (parseFloat(amortYears) || 25) * 12;
      if (PMT <= 0 || m <= 0 || n <= 0) return 0;
      return PMT * (Math.pow(1 + m, n) - 1) / (m * Math.pow(1 + m, n));
    }
    case 'amortization': {
      const P = parseFloat(balance) || 0;
      const PMT = parseFloat(monthlyPayment) || 0;
      if (P <= 0 || PMT <= 0 || m <= 0) return 0;
      const ratio = P * m / PMT;
      if (ratio >= 1) return 99;
      const n = -Math.log(1 - ratio) / Math.log(1 + m);
      return Math.ceil(n / 12);
    }
    default:
      return 0;
  }
}

// ─── Constants ──────────────────────────────────────────────────────

export const ENTITY_TYPES = [
  { value: 'personnel', label: 'Personnel' },
  { value: 'corporation', label: 'Corporation / Inc.' },
];

export const DEFAULT_TAX_RATES = {
  personnel: 53.31,
  corporation: 26.50,
};

export const CAPITAL_GAINS_INCLUSION = 0.50;

export const QC_DIVIDEND_TAX = {
  nonEligible: { grossUp: 0.15, dtcRate: 0.1450 },
  eligible: { grossUp: 0.38, dtcRate: 0.2074 },
};

export function calcDividendTax(dividendAmount, personalMarginalRate, isEligible = false) {
  if (dividendAmount <= 0) return 0;
  const params = isEligible ? QC_DIVIDEND_TAX.eligible : QC_DIVIDEND_TAX.nonEligible;
  const grossedUp = dividendAmount * (1 + params.grossUp);
  const tax = grossedUp * (personalMarginalRate / 100) - grossedUp * params.dtcRate;
  return Math.max(0, tax);
}

export const ASSET_TYPES = [
  { value: 'reer', label: 'REER' },
  { value: 'celi', label: 'CELI' },
  { value: 'non_enregistre', label: 'Non-enregistré' },
  { value: 'corporatif', label: 'Compte corporatif' },
  { value: 'immobilier', label: 'Immobilier' },
  { value: 'assurance_participative', label: 'Assurance participative' },
  { value: 'autre', label: 'Autre' },
];

export const DEBT_TYPES = [
  { value: 'hypotheque', label: 'Hypothèque' },
  { value: 'marge_credit', label: 'Marge de crédit' },
  { value: 'pret_auto', label: 'Prêt automobile' },
  { value: 'pret_personnel', label: 'Prêt personnel' },
  { value: 'carte_credit', label: 'Carte de crédit' },
  { value: 'autre', label: 'Autre' },
];

export const REVENUE_TYPES = [
  { value: 'salaire', label: 'Salaire / Emploi' },
  { value: 'revenu_immeuble', label: "Revenu d'immeuble" },
  { value: 'revenu_placement', label: 'Revenu de placement' },
  { value: 'dividende', label: 'Dividende' },
  { value: 'autre', label: 'Autre' },
];

export const EVENT_TYPES = [
  { value: 'refinancement', label: 'Refinancement' },
  { value: 'achat', label: 'Nouvel achat' },
  { value: 'vente', label: 'Vente' },
  { value: 'renovation', label: 'Rénovation' },
  { value: 'produit_financier', label: 'Produit financier' },
  { value: 'assurance_participative', label: 'Assurance participative' },
  { value: 'changement_contributions', label: 'Changement des contributions' },
  { value: 'cashflow_direction', label: 'Ajuster la direction du cashflow' },
  { value: 'cotisation_reer', label: 'Cotisation REER' },
  { value: 'retrait_reer', label: 'Retrait REER' },
  { value: 'cotisation_celi', label: 'Cotisation CELI' },
  { value: 'retrait_celi', label: 'Retrait CELI' },
  { value: 'plan_financement_immediat', label: 'Plan financement immédiat (PFI)' },
  { value: 'avance_sur_police', label: 'Avance sur police (imposable)' },
  { value: 'declaration_dividende', label: 'Déclaration de dividende' },
  { value: 'autre', label: 'Autre' },
];

export const insuranceService = makeSupaCrud('planning_assets', 'insurance');

// ─── Insurance file parser ──────────────────────────────────────────

export function parseInsuranceFile(workbook) {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  let yearlyData = [];
  let surrenderData = {};
  let insuranceData = {};
  let premiumData = {};

  for (let i = 0; i < raw.length; i++) {
    const row = raw[i];
    if (!row || row.length === 0) continue;
    const rowStr = row.map(c => String(c || '').toLowerCase()).join('|');
    const hasYear = rowStr.includes('année') || rowStr.includes('annee') || rowStr.includes('year');
    if (!hasYear) continue;

    let yearCol = -1, ageCol = -1, surrenderCol = -1, insuranceCol = -1, premiumCol = -1;
    for (let c = 0; c < row.length; c++) {
      const cell = String(row[c] || '').toLowerCase().trim();
      if (cell === 'année' || cell === 'annee' || cell === 'year') yearCol = c;
      if (cell === 'âge' || cell === 'age') ageCol = c;
      if (cell.includes('valeur de rachat totale') && surrenderCol === -1) surrenderCol = c;
      if ((cell.includes('assurance vie total') || cell.includes("d'assurance vie total")) && insuranceCol === -1) insuranceCol = c;
      if (cell.includes('primes') && cell.includes('versées') && premiumCol === -1) premiumCol = c;
    }
    if (yearCol === -1) continue;

    for (let j = i + 1; j < raw.length; j++) {
      const dataRow = raw[j];
      if (!dataRow || dataRow.length === 0) break;
      const yearVal = parseFloat(dataRow[yearCol]);
      if (isNaN(yearVal) || yearVal < 0) break;
      const year = Math.round(yearVal);
      if (surrenderCol !== -1) {
        const val = parseFloat(String(dataRow[surrenderCol]).replace(/[^0-9.-]/g, ''));
        if (!isNaN(val)) surrenderData[year] = val;
      }
      if (insuranceCol !== -1) {
        const val = parseFloat(String(dataRow[insuranceCol]).replace(/[^0-9.-]/g, ''));
        if (!isNaN(val)) insuranceData[year] = val;
      }
      if (premiumCol !== -1) {
        const val = parseFloat(String(dataRow[premiumCol]).replace(/[^0-9.-]/g, ''));
        if (!isNaN(val)) premiumData[year] = val;
      }
      if (ageCol !== -1) {
        const age = parseFloat(dataRow[ageCol]);
        if (!isNaN(age) && !yearlyData.find(d => d.year === year)) {
          yearlyData.push({ year, age: Math.round(age) });
        }
      }
    }
  }

  const allYears = new Set([
    ...Object.keys(surrenderData).map(Number),
    ...Object.keys(insuranceData).map(Number),
    ...Object.keys(premiumData).map(Number),
    ...yearlyData.map(d => d.year),
  ]);

  return Array.from(allYears).sort((a, b) => a - b).map(year => {
    const existing = yearlyData.find(d => d.year === year) || {};
    return {
      year,
      age: existing.age || null,
      surrenderValue: surrenderData[year] || 0,
      totalInsurance: insuranceData[year] || 0,
      premiumPaid: premiumData[year] || 0,
    };
  });
}
